// LivraisonsMobile.jsx — onglet « Livraisons » en version mobile.
//
// Réutilise la couche API déjà exportée par le desktop (gestion-vente/deliveryApi.ts) :
// aucun appel n'est réécrit ici. Comme le desktop, un bordereau ne se modifie ni ne se
// supprime — on le consulte, on l'émet depuis une vente éligible, on le réimprime.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ProductScreen, Appbar, SearchBar, Segmented, EmptyState, Sheet, ActionRow,
  DetailRow, Note, Field, TextInput, TextAreaInput, Pill, Mono, Ico,
  fmt, fmtMoney, useMobileMenu,
} from "../produits/productMobileKit";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import {
  fetchEligibleSalesForDelivery,
  fetchSalesDeliveries,
  fetchSalesDeliveryDetail,
  createSalesDelivery,
  downloadSalesDeliveryPdf,
} from "../../gestion-vente/deliveryApi";
import { getSalesTabsForUser } from "../../gestion-vente/SalesTabsLayout";
import {
  PERM, clientNameOf, deliveredPill, fmtDate, fmtDateTime, paymentPill,
  pdfNameOf, toLocalDateTimeValue,
} from "./livraisonsMobileShared";

const PAGE = 20;

const TABS = [
  { value: "pending", label: "À livrer" },
  { value: "history", label: "Bordereaux" },
];

const idOf = (v) => (v && typeof v === "object" ? v.id : v);

export default function LivraisonsMobile({ onMenu: onMenuProp }) {
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();

  const companyId = idOf(selectedCompany);
  const warehouseId = idOf(selectedWarehouse);

  const can = {
    create: hasPermission(PERM.create),
    print: hasPermission(PERM.generatePdf),
  };

  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const debounceRef = useRef(null);

  const [navOpen, setNavOpen] = useState(false);

  // Feuille détail (onglet Bordereaux)
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Feuille d'émission (onglet À livrer)
  const [sale, setSale] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);

  const salesTabs = useMemo(
    () =>
      getSalesTabsForUser(user, selectedCompany).filter(
        (t) => hasPermission(t.permission) && !t.path.endsWith("/livraisons")
      ),
    [user, selectedCompany, hasPermission]
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setAppliedSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  // Le desktop cherche par n° de facture sur les ventes éligibles et par n° de bordereau
  // sur l'historique : deux paramètres distincts (buildApiFilters, l.333-343).
  const load = useCallback(
    async (p) => {
      const term = appliedSearch.trim();
      const base = {
        company_id: companyId,
        warehouse_id: warehouseId,
        page: p,
        limit: PAGE,
      };
      setLoading(true);
      try {
        const res =
          tab === "pending"
            ? await fetchEligibleSalesForDelivery({
                ...base,
                ...(term ? { invoice_number: term } : {}),
              })
            : await fetchSalesDeliveries({
                ...base,
                status: "delivered",
                ...(term ? { delivery_number: term } : {}),
              });
        const incoming = (tab === "pending" ? res.eligibleSales : res.deliveries) || [];
        setRows((prev) => (p === 1 ? incoming : [...prev, ...incoming]));
        setTotal(res.pagination?.total || 0);
      } catch (err) {
        message.error(err?.message || "Impossible de charger les livraisons");
      } finally {
        setLoading(false);
      }
    },
    [tab, companyId, warehouseId, appliedSearch]
  );

  useEffect(() => { setPage(1); load(1); }, [load]);

  const loadMore = () => { const p = page + 1; setPage(p); load(p); };
  const reload = () => { setPage(1); load(1); };

  const switchTab = (v) => { if (v !== tab) { setRows([]); setTotal(0); setTab(v); } };

  /* ---------- détail d'un bordereau ---------- */
  const openDetail = async (row) => {
    setDetail(row);
    setDetailLoading(true);
    try {
      const full = await fetchSalesDeliveryDetail(row.id, { company_id: companyId });
      setDetail(full);
    } catch (err) {
      message.error(err?.message || "Impossible de charger le bordereau");
    } finally {
      setDetailLoading(false);
    }
  };
  const closeDetail = () => { setDetail(null); };

  const printPdf = async (delivery) => {
    try {
      await downloadSalesDeliveryPdf(delivery.id, pdfNameOf(delivery));
    } catch (err) {
      message.error(err?.message || "Erreur lors du téléchargement du bordereau.");
    }
  };

  /* ---------- émission d'un bordereau ---------- */
  const openCreate = (row) => {
    if (!can.create) return message.warning("Vous n'avez pas le droit d'émettre un bordereau.");
    setSale(row);
    setForm({
      delivery_date: toLocalDateTimeValue(),
      recipient_name: row.client_name || "",
      recipient_phone: "",
      notes: "",
    });
  };
  const closeCreate = () => { if (!saving) { setSale(null); setForm(null); } };

  const formValid = Boolean(form?.delivery_date) && Boolean(form?.recipient_name.trim());

  const submit = async () => {
    if (!formValid) return message.error("La date de livraison et le destinataire sont obligatoires.");
    if (submitting.current) return;
    submitting.current = true;
    setSaving(true);
    try {
      const res = await createSalesDelivery({
        order_id: sale.id,
        company_id: companyId,
        // Le champ natif rend une heure locale ; le desktop envoie un ISO 8601.
        delivery_date: new Date(form.delivery_date).toISOString(),
        recipient_name: form.recipient_name.trim(),
        recipient_phone: form.recipient_phone.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      message.success(res.message || "Bordereau émis avec succès.");
      setSale(null); setForm(null);
      reload();
      if (res.delivery && can.print) await printPdf(res.delivery);
    } catch (err) {
      message.error(err?.message || "Erreur lors de l'émission du bordereau.");
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  /* ---------- rendu ---------- */
  const items = detail?.items || [];

  return (
    <ProductScreen>
      <Appbar
        title="Livraisons"
        onMenu={onMenu}
        subtitle={
          tab === "pending"
            ? `${fmt(total)} vente${total > 1 ? "s" : ""} à livrer`
            : `${fmt(total)} bordereau${total > 1 ? "x" : ""}`
        }
        action={
          salesTabs.length ? (
            <button className="iconbtn" aria-label="Aller à un autre onglet" onClick={() => setNavOpen(true)}>
              <Ico name="list-ordered" />
            </button>
          ) : null
        }
      />

      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <Segmented value={tab} onChange={switchTab} options={TABS} />
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder={tab === "pending" ? "Rechercher un n° de facture…" : "Rechercher un n° de bordereau…"}
        />
      </div>

      <div className="body">
        {loading && !rows.length ? (
          <EmptyState loading title="Chargement…" text="Synchronisation en cours." />
        ) : !rows.length ? (
          <EmptyState
            icon="truck"
            title={appliedSearch ? "Aucun résultat" : tab === "pending" ? "Aucune vente à livrer" : "Aucun bordereau"}
            text={
              appliedSearch
                ? "Aucune ligne ne correspond à votre recherche."
                : tab === "pending"
                ? "Les ventes livrables apparaîtront ici."
                : "Émettez un bordereau depuis une vente à livrer."
            }
          />
        ) : (
          <div className="list">
            {rows.map((r) =>
              tab === "pending" ? (
                <EligibleCard key={r.id} sale={r} onClick={() => openCreate(r)} />
              ) : (
                <DeliveryCard key={r.id} delivery={r} onClick={() => openDetail(r)} />
              )
            )}

            {rows.length < total ? (
              <button className="btn btn--ghost" style={{ width: "100%" }} onClick={loadMore} disabled={loading}>
                <Ico name={loading ? "loader-circle" : "list-ordered"} className={loading ? "spin" : ""} />
                Charger plus ({rows.length}/{total})
              </button>
            ) : null}
          </div>
        )}
      </div>

      <Sheet open={navOpen} onClose={() => setNavOpen(false)} title="Aller à…" subtitle="Autres écrans du module Ventes.">
        {salesTabs.map((t) => (
          <ActionRow key={t.key} icon="chevron-right" label={t.label}
            onClick={() => { setNavOpen(false); navigate(t.path); }} />
        ))}
      </Sheet>

      {/* feuille : détail d'un bordereau */}
      <Sheet
        open={Boolean(detail)}
        onClose={closeDetail}
        title={detail?.delivery_number}
        subtitle={detail ? clientNameOf(detail) : undefined}
      >
        {detail ? (
          <>
            <DetailRow label="Date de livraison" value={fmtDateTime(detail.delivery_date)} />
            <DetailRow label="Facture liée" value={detail.invoice_number || detail.invoice_number_snapshot || "—"} />
            <DetailRow label="Établissement" value={detail.warehouse_name || "—"} />
            <DetailRow label="Émis par" value={detail.delivered_by_name || detail.delivered_by_name_snapshot || "—"} />
            <DetailRow label="Destinataire" value={detail.recipient_name || "—"} />
            <DetailRow label="Téléphone" value={detail.recipient_phone || "—"} />
            <DetailRow label="Statut livraison" value={deliveredPill().label} />
            <DetailRow label="Statut paiement" value={paymentPill(detail.payment_status).label} />

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Articles livrés</div>
            {detailLoading && !items.length ? (
              <EmptyState loading title="Chargement…" />
            ) : !items.length ? (
              <Note>Aucun article à afficher.</Note>
            ) : (
              items.map((it, i) => (
                <DetailRow
                  key={it.id ?? i}
                  label={it.product_name_snapshot}
                  value={`${fmt(it.quantity)} ${it.unit_short_name_snapshot || it.unit_name_snapshot || ""}`.trim()}
                />
              ))
            )}

            {detail.notes ? (
              <>
                <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Remarques</div>
                <Note>{detail.notes}</Note>
              </>
            ) : null}

            {can.print ? (
              <div style={{ marginTop: 16 }}>
                <ActionRow icon="printer" label="Réimprimer le PDF" onClick={() => printPdf(detail)} />
              </div>
            ) : null}
          </>
        ) : null}
      </Sheet>

      {/* feuille : émission d'un bordereau */}
      <Sheet
        open={Boolean(sale)}
        onClose={closeCreate}
        title="Émettre un bordereau"
        subtitle={sale ? `Vente ${sale.invoice_number}` : undefined}
      >
        {form && sale ? (
          <>
            <DetailRow label="Client" value={clientNameOf(sale)} />
            <DetailRow label="Établissement" value={sale.warehouse_name || "—"} />
            <DetailRow label="Montant" value={fmtMoney(sale.total)} />

            <Field label="Date et heure de livraison *">
              <div className="ctrl">
                <input
                  type="datetime-local"
                  value={form.delivery_date}
                  onChange={(e) => setForm((f) => ({ ...f, delivery_date: e.target.value }))}
                />
              </div>
            </Field>
            <Field label="Destinataire *">
              <TextInput value={form.recipient_name}
                onChange={(v) => setForm((f) => ({ ...f, recipient_name: v }))} />
            </Field>
            <Field label="Téléphone" hint="facultatif">
              <TextInput value={form.recipient_phone} inputMode="tel"
                onChange={(v) => setForm((f) => ({ ...f, recipient_phone: v }))} />
            </Field>
            <Field label="Remarques" hint="facultatif">
              <TextAreaInput value={form.notes} rows={2}
                onChange={(v) => setForm((f) => ({ ...f, notes: v }))} />
            </Field>

            {can.print ? <Note>Le PDF du bordereau sera téléchargé automatiquement après l'émission.</Note> : null}

            <div className="sheet__actions">
              <button className="btn btn--ghost" onClick={closeCreate} disabled={saving}>Annuler</button>
              <button className="btn btn--primary" onClick={submit} disabled={saving || !formValid}>
                <Ico name={saving ? "loader-circle" : "check"} className={saving ? "spin" : ""} />
                Émettre et valider
              </button>
            </div>
          </>
        ) : null}
      </Sheet>
    </ProductScreen>
  );
}

function EligibleCard({ sale, onClick }) {
  const pay = paymentPill(sale.payment_status);
  return (
    <button className="rcard" onClick={onClick}>
      <Mono name={sale.invoice_number || "V"} />
      <div className="rcard__main">
        <div className="rcard__name">{sale.invoice_number}</div>
        <div className="rcard__meta">
          <Pill tone={pay.tone} label={pay.label} />
        </div>
        <div className="rcard__meta">
          <span className="rcard__sub">{fmtDate(sale.order_date)} · {clientNameOf(sale)}</span>
        </div>
      </div>
      <span className="rcard__price">{fmtMoney(sale.total)}</span>
    </button>
  );
}

function DeliveryCard({ delivery, onClick }) {
  const state = deliveredPill();
  return (
    <button className="rcard" onClick={onClick}>
      <Mono name={delivery.delivery_number || "B"} />
      <div className="rcard__main">
        <div className="rcard__name">{delivery.delivery_number}</div>
        <div className="rcard__meta">
          <Pill tone={state.tone} label={state.label} />
          {delivery.invoice_number ? <Pill tone="info" label={delivery.invoice_number} /> : null}
        </div>
        <div className="rcard__meta">
          <span className="rcard__sub">{fmtDate(delivery.delivery_date)} · {clientNameOf(delivery)}</span>
        </div>
      </div>
      <span className="rcard__chev"><Ico name="chevron-right" /></span>
    </button>
  );
}
